import { AlertCircle, CheckCircle, Edit, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ModerationQueueItem } from "@/types/api";

interface AdminReviewGridProps {
  providers: ModerationQueueItem[];
  isProcessing: boolean;
  handleEditListing: (id: string) => void;
  handleStatusChange: (id: string, status: "approved" | "rejected") => void;
}

const AdminReviewGrid = ({
  providers,
  isProcessing,
  handleEditListing,
  handleStatusChange,
}: AdminReviewGridProps) => {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
      {providers.map((provider) => (
        <div
          key={provider.listingId}
          className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          <div className="relative h-48 bg-gray-100">
            {provider.imageUrl ? (
              <img
                src={provider.imageUrl}
                alt={provider.name}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="flex h-full items-center justify-center bg-gray-100">
                <AlertCircle className="h-12 w-12 text-gray-300" />
              </div>
            )}
          </div>

          <div className="flex flex-1 flex-col p-5">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">{provider.name}</h3>
              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                {provider.status}
              </Badge>
              {provider.flags.isUnassigned ? (
                <Badge variant="outline" className="border-gray-200 bg-gray-100 text-gray-700">
                  Unassigned
                </Badge>
              ) : null}
            </div>

            <p className="mt-3 line-clamp-3 text-sm leading-6 text-gray-500">{provider.description}</p>

            <div className="mt-4 flex flex-wrap gap-1">
              {(provider.categories || [provider.category]).map((category) => (
                <Badge key={category} variant="outline" className="border-gray-200 bg-gray-100 text-gray-600">
                  {category}
                </Badge>
              ))}
            </div>

            {provider.suburbs && provider.suburbs.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-1">
                {provider.suburbs.slice(0, 4).map((suburb) => (
                  <Badge key={suburb} variant="outline" className="border-gray-200 bg-gray-100 text-gray-600">
                    {suburb}
                  </Badge>
                ))}
                {provider.suburbs.length > 4 ? (
                  <Badge variant="outline" className="border-gray-200 bg-gray-100 text-gray-600">
                    +{provider.suburbs.length - 4} more
                  </Badge>
                ) : null}
              </div>
            ) : null}

            <div className="mt-5 grid gap-2 sm:grid-cols-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
                onClick={() => handleEditListing(provider.listingId)}
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
              <Button
                type="button"
                size="sm"
                className="rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark"
                onClick={() => handleStatusChange(provider.listingId, "approved")}
                disabled={isProcessing}
              >
                <CheckCircle className="h-4 w-4" />
                Approve
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                className="rounded-lg"
                onClick={() => handleStatusChange(provider.listingId, "rejected")}
                disabled={isProcessing}
              >
                <XCircle className="h-4 w-4" />
                Reject
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminReviewGrid;
